import {
  type AdaptiveLearningAiGeneratedMaterialItem,
  type InjectableActivity,
  getModuleHasInjectableActivities,
  injectAdaptiveLearningAiGeneratedItems,
  injectItemsIntoCourseMaterials,
  mergeAdaptiveLearningAiGeneratedItems,
  replaceAdaptiveLearningAiGeneratedItems,
} from '@coursera-web/ondemand-common';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import path from 'js/lib/path';

import Course from 'pages/open-course/common/models/course';
import CourseMaterials from 'pages/open-course/common/models/courseMaterials';

import { GradePolicy } from 'bundles/in-course-stores/constants/Constants';
import type {
  CourseMaterialsData,
  CourseTypeMetadata,
  RawCourseMaterials,
} from 'bundles/in-course-stores/constants/CourseType';
import { CourseType } from 'bundles/in-course-stores/constants/CourseType';
import type { GradedAssignmentGroupNormalized } from 'bundles/learner-progress/types/GradedAssignmentGroup';
import type { SharedPortfolioProject_UserPortfolioProjectFragment as PortfolioProject } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';

const SERIALIZED_PROPS: (keyof CourseStore$DehydratedState)[] = [
  'loaded',
  'courseIdentifiersLoaded',
  'domains',
  'courseId',
  'courseSlug',
  'courseCertificates',
  'courseTypeMetadata',
  'rawCourseMaterials',
  'portfolioProject',
];

type CourseStore$DehydratedState = {
  loaded: boolean;
  courseIdentifiersLoaded: boolean;
  domains: any;
  courseId: string;
  courseSlug: string;
  courseCertificates: Array<string>;
  courseTypeMetadata: CourseTypeMetadata;
  rawCourseMaterials: RawCourseMaterials;
  portfolioProject: PortfolioProject | null;
};

/**
 * This store is intended to encapsulate any and all questions
 * regarding the materials and metadata of a course.
 */
class CourseStore extends BaseStore implements CourseStore$DehydratedState {
  static storeName = 'CourseStore';

  static handlers = {
    SET_INJECTABLE_ACTIVITIES: 'onSetInjectableActivities',
    SET_ADAPTIVE_LEARNING_AI_GENERATED_ITEMS: 'onSetAdaptiveLearningAiGeneratedItems',
    FINALIZE_ADAPTIVE_LEARNING_AI_GENERATED_ITEMS: 'onFinalizeAdaptiveLearningAiGeneratedItems',
    LOAD_COURSE_MATERIALS: 'onLoadMaterials',
    LOAD_COURSE_SCHEDULE: 'onLoadCourseSchedule',
    LOAD_DOMAINS: 'onLoadDomains',
    LOADING_DOMAINS_ERROR: 'onLoadDomainsError',
    SET_COURSE_IDENTIFIERS: 'onSetCourseIdentifiers',
    SET_PORTFOLIO_PROJECT: 'onSetPortfolioProject',
  };

  onLoadCourseSchedule() {
    this.dispatcher.waitFor('CourseScheduleStore', () => {
      this.setModuleWeeks();
    });
  }

  onLoadDomains(domains: $TSFixMe /* TODO: type domains */) {
    this.domains = domains;
    this.domainsLoadingError = undefined;
    this.emitChange();
  }

  onLoadDomainsError(error: Error) {
    Sentry.captureException(error);
    this.domainsLoadingError = error;
    this.domains = [];
    this.emitChange();
  }

  onSetCourseIdentifiers({
    courseId,
    courseSlug,
    courseCertificates,
    courseTypeMetadata,
  }: {
    courseId: string;
    courseSlug: string;
    courseCertificates: Array<string>;
    courseTypeMetadata: CourseTypeMetadata;
  }) {
    this.courseId = courseId;
    this.courseSlug = courseSlug;
    this.courseCertificates = courseCertificates;
    this.courseTypeMetadata = courseTypeMetadata;
    this.courseIdentifiersLoaded = true;
    this.emitChange();
  }

  onSetPortfolioProject(project: PortfolioProject | null) {
    this.portfolioProject = project;
    this.emitChange();
  }

  loaded = false;

  courseIdentifiersLoaded = false;

  declare domains: any;

  declare domainsLoadingError?: Error;

  declare courseId: string;

  declare courseSlug: string;

  declare courseCertificates: Array<string>;

  declare courseTypeMetadata: CourseTypeMetadata;

  rawCourseMaterials: RawCourseMaterials = {};

  injectableActivities: InjectableActivity[] = [];

  /**
   * Id + display name for adaptive AI placeholders returned by the AdaptiveLearning GraphQL onboard mutation.
   * Passed into `injectAdaptiveLearningAiGeneratedItems` when course materials are built.
   */
  adaptiveLearningAiGeneratedItems: AdaptiveLearningAiGeneratedMaterialItem[] = [];

  /**
   * Map of moduleId to boolean indicating if the module has injectable
   * activities.
   */
  moduleHasInjectableActivities: Record<string, boolean> = {};

  materials: any = new CourseMaterials({}, { course: new Course() });

  portfolioProject: PortfolioProject | null = null;

  dehydrate(): CourseStore$DehydratedState {
    return _.pick(this, ...SERIALIZED_PROPS);
  }

  rehydrate(state: CourseStore$DehydratedState) {
    Object.assign(this, _.pick(state, ...SERIALIZED_PROPS));
    this.initializeMaterials();
  }

  /**
   * This method is expected to be called once, before `onLoadMaterials()`.
   * This way we support "content-based" injections. For the future
   * "behavior-based" injections, we will dispatch
   * "SET_INJECTABLE_ACTIVITIES" with the updated list of injectable activities
   * and call `initializeMaterials()` again.
   */
  onSetInjectableActivities({ activities }: { activities: InjectableActivity[] }) {
    this.injectableActivities = activities;

    if (this.loaded) {
      this.initializeMaterials();
    }
  }

  onSetAdaptiveLearningAiGeneratedItems({ items }: { items: AdaptiveLearningAiGeneratedMaterialItem[] }) {
    this.adaptiveLearningAiGeneratedItems = items;

    if (!this.loaded) {
      return;
    }

    // Phase 1 of a regeneration: add the new AI items while KEEPING the old ones, so the route's
    // current item stays resolvable (CourseFrameItemPage doesn't hit `return null`, which would
    // unmount the whole frame — flash — and tear down the left-nav animation). Phase 2 (finalize)
    // prunes the stale items after navigation lands. Fall back to a full rebuild only when the
    // synthetic lesson isn't present yet (the first time items are injected).
    if (mergeAdaptiveLearningAiGeneratedItems(this.materials, items)) {
      this.emitChange();
    } else {
      this.initializeMaterials();
    }
  }

  /**
   * Phase 2 of two-phase regen: prune stale items from the synthetic AI lesson, leaving only the
   * current canonical set. Called after navigation has landed on the new item, so the old route's
   * item is no longer needed — safe to remove without a flash because the route now points at a
   * valid (new) item.
   */
  onFinalizeAdaptiveLearningAiGeneratedItems() {
    if (!this.loaded) {
      return;
    }
    replaceAdaptiveLearningAiGeneratedItems(this.materials, this.adaptiveLearningAiGeneratedItems);
    this.emitChange();
  }

  onLoadMaterials({ rawCourseMaterials }: { rawCourseMaterials: RawCourseMaterials }) {
    this.loaded = true;
    this.rawCourseMaterials = rawCourseMaterials;

    this.initializeMaterials();
  }

  initializeMaterials() {
    const { courseData, courseMaterialsData } = this.rawCourseMaterials;
    const course = new Course(courseData, { parse: true });

    this.moduleHasInjectableActivities = getModuleHasInjectableActivities(this.injectableActivities);
    const afterInjectable = injectItemsIntoCourseMaterials(courseMaterialsData, this.injectableActivities);
    const updatedCourseMaterialsData = injectAdaptiveLearningAiGeneratedItems(
      afterInjectable as CourseMaterialsData,
      this.adaptiveLearningAiGeneratedItems
    );
    this.materials = new CourseMaterials(updatedCourseMaterialsData, {
      course,
      parse: true,
    });

    // NOTE: non-react exam pages depend on weeks to be set up.
    // can remove once converted to react.
    // emitChange happens in setModuleWeeks()
    this.setModuleWeeks();
  }

  setModuleWeeks() {
    const schedule = this.dispatcher.getStore('CourseScheduleStore').getSchedule();

    this.getMaterials()
      .getModules()
      .forEach((module: $TSFixMe /* TODO: type CourseMaterials */) => {
        module.set('week', schedule.getWeekForModuleId(module.get('id')));
      });

    this.emitChange();
  }

  hasLoaded(): boolean {
    return this.loaded;
  }

  haveCourseIdentifiersLoaded(): boolean {
    return this.courseIdentifiersLoaded;
  }

  getCourseId(): string {
    return this.courseId;
  }

  getCourseSlug(): string {
    return this.courseSlug;
  }

  getCourseName(): string {
    return this.materials.get('course.name');
  }

  getCourseRootPath(): string {
    return path.join('/learn', this.courseSlug);
  }

  getModule(moduleId: string): any {
    return this.materials.getModules().get(moduleId);
  }

  getMaterials(): any {
    return this.materials;
  }

  hasSessions(): boolean {
    return this.getMetadata().hasSessions();
  }

  isGuidedProject(): boolean {
    return this.courseTypeMetadata && this.courseTypeMetadata.typeName === CourseType.RHYME_PROJECT;
  }

  isVerifiedSkillsCourse(): boolean {
    return this.courseTypeMetadata && this.courseTypeMetadata.typeName === CourseType.VERIFIED_SKILLS_COURSE;
  }

  getMetadata(): any {
    if (this.materials) {
      return this.materials.get('course');
    }

    return undefined;
  }

  isCumulativeGradePolicy() {
    return !!this.getGradePolicy()[GradePolicy.CUMULATIVE];
  }

  isMasteryGradePolicy() {
    return !!this.getGradePolicy()[GradePolicy.MASTERY];
  }

  isMixedGradePolicy() {
    return this.isCumulativeGradePolicy() && this.hasItemsRequiredForPassing();
  }

  getGradePolicy() {
    return this.materials.get('gradePolicy');
  }

  getGradingParameters() {
    return this.materials.get('gradingParameters');
  }

  hasItemsRequiredForPassing() {
    return (
      this.materials
        .getGradableItemMetadatas()
        .filter((itemMetadata: $TSFixMe /* TODO: type CourseMaterials */) => itemMetadata.isPassRequiredForCourse())
        .length > 0
    );
  }

  getPartnerId(): number | undefined {
    return this.getMetadata()?.get('partnerIds')?.[0];
  }

  getPartnerIds(): number[] {
    return this.getMetadata()?.get('partnerIds') ?? [];
  }

  isPreEnrollEnabledForCourse(): boolean {
    return this.getMetadata().isPreEnrollEnabled();
  }

  isPreEnrollEnabledForUser(): boolean {
    const isEnrolled = this.dispatcher.getStore('CourseMembershipStore').isEnrolled();

    return (
      !isEnrolled &&
      this.isPreEnrollEnabledForCourse() &&
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      (!getCourseModeOverride() || getCourseModeOverride() === courseModes.PRE_ENROLL)
    );
  }

  isSubtitleTranslationEnabledForCourse(): boolean {
    return this.getMetadata().getIsSubtitleTranslationEnabled();
  }

  getGradableItems(): any {
    if (this.materials) {
      return this.materials.getGradableItemMetadatas();
    }

    return undefined;
  }

  // hasPeerReviewAssignments is only used in static/bundles/course-home/page-course-info/components/HowStuffWorks.tsx
  hasPeerReviewAssignments(): boolean {
    const peerReviewMaterials = this.materials.getPeerReviewItemMetadatas();
    return !!peerReviewMaterials.models.length;
  }

  getGradedAssignmentGroups(): Array<GradedAssignmentGroupNormalized> {
    const { gradedAssignmentGroups } = this.getGradingParameters();
    const gradedAssignmentGroupIds = Object.keys(gradedAssignmentGroups);
    return gradedAssignmentGroupIds.map((id) => {
      const gradedAssignmentGroup = gradedAssignmentGroups[id];
      return { id, ...gradedAssignmentGroup };
    });
  }

  // hasProgrammingAssignments is only used in static/bundles/course-home/page-course-info/components/HowStuffWorks.tsx
  hasProgrammingAssignments(): boolean {
    const programmingMaterials = this.materials.getProgrammingItemMetadatas();
    return !!programmingMaterials.models.length;
  }

  isFirstPeerReview(peerReviewId: string): boolean {
    if (this.materials) {
      const peerReviewMaterials = this.materials.getPeerReviewItemMetadatas();
      if (peerReviewMaterials.models.length) {
        return peerReviewMaterials.models[0].get('id') === peerReviewId;
      }
    }

    return false;
  }

  // hasCertificates is only used in static/bundles/course-home/page-course-info/components/HowStuffWorks.tsx
  hasCertificates(): boolean {
    return this.courseCertificates && this.courseCertificates.length > 0;
  }

  // This is used only in static/bundles/programming-common/components/ProgrammingAssignmentInformation.tsx
  isFirstProgrammingAssignment(progAssignmentId: string): boolean {
    if (this.materials) {
      const progMaterials = this.materials.getProgrammingItemMetadatas();
      if (progMaterials.models.length) {
        return progMaterials.models[0].get('id') === progAssignmentId;
      }
    }

    return false;
  }

  getTotalGradingWeight(): number {
    return this.materials.get('totalGradingWeight');
  }

  getPortfolioProject(): PortfolioProject | null {
    return this.portfolioProject;
  }
}

export default CourseStore;
